import { FC, useEffect, useState, useCallback } from "react";
import { useTranslations } from "use-intl";
import { Typography } from "@blofin/blofin-ui";
import { numberFormat, transformUSDT } from "../utils/index";
import { useStoreState } from "../store/useStore";
import useAction, { NewUserTaskExpiredInfo } from "../AccountMode/useAction";
import useWatchLoaclstorageValue from "./useWatchLoaclstorageValue";
import { textWeight } from "@/nav-localized/config";
import clsx from "clsx";

import {
  assetPopperCardHover,
  assetPopperGroupHoverLabel,
  assetPopperQuickActionHover,
  dropDownStylesDesText,
  megaMenuDismissButton,
  megaMenuLabel60,
  stylesText
} from "../styles";

import DepositFillIcon from "@/nav-localized/assets/header/deposit-fill.svg?react";
import WithdrawFillIcon from "@/nav-localized/assets/header/withdraw-fill.svg?react";
import TransferFillIcon from "@/nav-localized/assets/header/transfer-fill.svg?react";

interface FiatList {
  name: string;
  precision: string;
  rate: string;
  symbol: string;
}

const defaultCrypto = {
  usdt_usd: "1",
  name: "USD",
  precision: "0.01",
  rate: "1",
  symbol: "$"
};

interface UnlockableRewardCardProps {
  taskInfo: NewUserTaskExpiredInfo;
  visible?: boolean;
  className?: string;
}

const UnlockableRewardCard: FC<UnlockableRewardCardProps> = ({ taskInfo, visible, className }) => {
  const { localeLink, theme, isDemoTrading, userInfo, userAccountType } = useStoreState();
  const isMainAccount = userInfo?.account_type === "MAIN" || userAccountType === "MAIN";
  const mode = theme === "dark" ? "dark" : "light";
  const t = useTranslations("header");

  const { getUserAsset, getExchangeRate } = useAction();

  const [userAsset, setUserAsset] = useState("0");
  const [loading, setLoading] = useState(true);
  const [isShow, setIsShow] = useState(true);
  const [crypto, setCrypto] = useState(defaultCrypto);

  const { updateLocalStorage, value: localstorageVisibility } = useWatchLoaclstorageValue({
    key: "assets-isHideTotalAssets",
    initValue: isShow ? "1" : "0"
  });

  const initAccountInfo = async () => {
    try {
      const res = await getUserAsset();
      const { code, msg, data } = res as unknown as {
        code: number;
        msg: string;
        data: { total_equity: string };
      };
      setLoading(false);
      if (code !== 200) {
        throw new Error(msg || "Get User Asset Error");
      }
      setUserAsset(data.total_equity);
    } catch (error) {
      console.error("Get User Asset Error", error);
    }
  };

  const getRate = async () => {
    try {
      const res = await getExchangeRate<{
        code: number;
        data: { fiat: FiatList[]; usdt_usd: string };
      }>();
      const { code, data } = res || {};
      if (code == 200) {
        const { usdt_usd = "", fiat = [] } = data || {};
        const USDInfo = fiat.find((item) => item.name === "USD") || defaultCrypto;
        setCrypto({ usdt_usd, ...USDInfo });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const changeShow = () => {
    const newValue = !isShow;
    setIsShow(newValue);
    updateLocalStorage(newValue ? "1" : "0");
  };

  useEffect(() => {
    const newValue = localstorageVisibility === "0" ? false : true;
    setIsShow(newValue);
  }, [localstorageVisibility]);

  useEffect(() => {
    if (visible) {
      getRate();
      initAccountInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const getTaskConfig = () => {
    if (!taskInfo.transfer_task_finish_status) {
      return {
        description: t("unlockableRewardTransferDesc", {
          amount: `\u200E${taskInfo.transfer_task_amount}\u200E`
        }),
        actionText: t("transferNow"),
        actionLink: `${localeLink}/assets/overview?transfer=true`
      };
    }

    if (taskInfo.transfer_task_finish_status && !taskInfo.trade_task_finish_status) {
      return {
        description: t("unlockableRewardTradeDesc", {
          amount: `\u200E${taskInfo.trade_task_amount}\u200E`
        }),
        actionText: t("tradeNow"),
        actionLink: `/futures${localeLink}/BTC-USDT`
      };
    }

    return {
      description: t("unlockableRewardAllDoneDesc", {
        amount: numberFormat(taskInfo.total_reward_amount, 2)
      }),
      actionText: t("exploreNow"),
      actionLink: `${localeLink}/task-center`
    };
  };

  const config = getTaskConfig();

  const handleActionClick = useCallback(() => {
    window?.reportTrack("Web_Heading_Window_Click", {
      link: config.actionLink
    });
    window.location.href = config.actionLink;
  }, [config.actionLink]);

  const quickActions = [
    {
      key: "deposit",
      Icon: DepositFillIcon,
      label: t("deposit"),
      onClick: () => {
        window.location.href = `${localeLink}/assets/deposit`;
      }
    },
    {
      key: "withdraw",
      Icon: WithdrawFillIcon,
      label: t("withdraw"),
      onClick: () => {
        window.location.href = `${localeLink}/assets/withdraw`;
      }
    },
    {
      key: "transfer",
      Icon: TransferFillIcon,
      label: t("transfer"),
      onClick: () => {
        window.location.href = `${localeLink}/assets/overview?transfer=true`;
      }
    }
  ];

  return (
    <div className={clsx("flex flex-col gap-[16px]", className)}>
      {/* Overview 余额区域 — 与 AssetsCard 样式一致 */}
      <div className="p-[12px] pb-0">
        <div
          className={clsx(
            "cursor-pointer group/assetcard px-[12px] py-[10px] rounded-[8px]",
            assetPopperCardHover({ mode })
          )}
          onClick={() => (window.location.href = `${localeLink}/assets/overview`)}
        >
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center">
              <Typography
                variant="body3"
                className={clsx("leading-[20px] font-medium", dropDownStylesDesText({ mode }))}
              >
                {t("overview")}
              </Typography>
              {isShow ? (
                <i
                  className={clsx(
                    "ml-1 iconfont icon-show-fill text-[14px] cursor-pointer",
                    megaMenuDismissButton({ mode })
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    changeShow();
                  }}
                />
              ) : (
                <i
                  className={clsx(
                    "ml-1 iconfont icon-hide-fill text-[14px] cursor-pointer",
                    megaMenuDismissButton({ mode })
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    changeShow();
                  }}
                />
              )}
            </div>
            <i
              className={clsx(
                "iconfont icon-arrow-right-l-line text-[16px]",
                assetPopperGroupHoverLabel({ mode })
              )}
            />
          </div>
          <Typography
            dir="ltr"
            variant="subtitle1"
            className={`leading-[34px] ${textWeight}`}
            theme={theme}
          >
            {isShow ? `${loading ? "--" : numberFormat(userAsset, 2)}` : "******"} USDT
          </Typography>
          <Typography
            dir="ltr"
            variant="body2"
            className={clsx("leading-[24px] font-normal", dropDownStylesDesText({ mode }))}
          >
            {isShow
              ? `≈ ${loading ? "--" : transformUSDT(userAsset, Number(crypto.usdt_usd) ? Number(crypto.usdt_usd) : 1, Number(crypto.rate) ? Number(crypto.rate) : 1, crypto.precision, crypto.symbol)}`
              : "******"}
          </Typography>
        </div>
      </div>

      {/* 任务提示卡片 */}
      <div className="px-[12px]">
        <div
          className={clsx(
            "flex flex-col gap-[4px] items-start justify-center px-[12px] py-[8px] rounded-[8px] border border-solid",
            mode === "dark" ? "border-dark-line-primary" : "border-light-line-primary"
          )}
        >
          <p className={clsx("text-[12px] leading-[18px]", megaMenuLabel60({ mode }))}>
            {config.description}
          </p>
          <div
            className="flex items-center gap-[4px] cursor-pointer rounded-[6px]"
            onClick={handleActionClick}
          >
            <p className="text-[12px] font-medium leading-[18px] text-base-primary">
              {config.actionText}
            </p>
            <i className="iconfont icon-arrow-right-s-line text-[16px] text-base-primary" />
          </div>
        </div>
      </div>

      {/* Deposit / Withdraw / Transfer 三图标（模拟盘和子账户下不展示） */}
      {!isDemoTrading && isMainAccount && (
        <div className="flex items-center gap-[16px] px-[24px] pb-[16px]">
          {quickActions.map((action) => (
            <div
              key={action.key}
              className="group/quickaction flex flex-[1_0_0] flex-col items-center justify-center gap-[4px] cursor-pointer"
              onClick={action.onClick}
            >
              <div
                className={clsx(
                  "flex items-center justify-center w-[40px] h-[40px] rounded-[8px] shrink-0",
                  mode === "dark" ? "bg-dark-fill-tertiary" : "bg-light-fill-secondary"
                )}
              >
                <action.Icon
                  className={clsx(
                    "w-[24px] h-[24px]",
                    stylesText({ mode }),
                    assetPopperQuickActionHover({ mode })
                  )}
                />
              </div>
              <p
                className={clsx(
                  "text-[12px] leading-[18px] text-center",
                  stylesText({ mode }),
                  assetPopperQuickActionHover({ mode })
                )}
              >
                {action.label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnlockableRewardCard;
